import { type InjectedFormikProps } from "formik";

import { BTPopover } from "@btnet/core-components/BTPopover";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { IDTypes } from "commonComponents/entity/accounting/common/accounting.api.types";
import {
    type UnlinkAccountingResponse,
    type UpdateAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import { getIntegrationTypeName } from "commonComponents/financial/Common/Accounting.types";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { AccountingLinkingCard } from "entity/accounting/AccountingLinkingCard/AccountingLinkingCard";
import {
    type IInternalUserFormValues,
    type InternalUserEntity,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";

interface IAccountingTabProps {
    entity: InternalUserEntity;
    onReloadUser: () => Promise<void>;
    onUpdateAccounting: (id: number) => Promise<UpdateAccountingResponse>;
    onUnlinkAccounting: (id: number) => Promise<UnlinkAccountingResponse>;
    onLinkEntityToAccounting: (selectedAccountingEmployee: string) => Promise<void>;
}

export function AccountingTab(
    props: InjectedFormikProps<IAccountingTabProps, IInternalUserFormValues>,
) {
    const { entity, values, setFieldValue, setFieldTouched, onReloadUser } = props;
    const integrationName = getIntegrationTypeName(
        entity.accountingEntity!.accountingIntegrationType,
    );

    const isNew = entity.internalUser.id === 0;
    return (
        <>
            <PageSection title={integrationName}>
                <AccountingLinkingCard
                    entity={entity.accountingEntity}
                    linkByDefault={false}
                    isNew={isNew}
                    btEntityName={`${entity.internalUser.firstName} ${entity.internalUser.lastName}`}
                    idType={IDTypes.EmployeeID}
                    errorValidationText={`Please select a ${integrationName} ${entity.accountingEntity!.primaryAccountingTerm.toLocaleLowerCase()} to link to.`}
                    actionFieldName="createNewAccountingEntityOption"
                    linkTypeFieldName="accountingEntityLinkingType"
                    onUpdateAccounting={props.onUpdateAccounting}
                    onUnlinkFromAccounting={props.onUnlinkAccounting}
                    onReloadAccounting={onReloadUser}
                    onLinkEntityToAccounting={props.onLinkEntityToAccounting}
                />
                {entity.payrollItems && (
                    <div className="margin-top-lg">
                        <BTTitle level={2}>Time Clock Options</BTTitle>
                        <BTCol xs={16} md={8} lg={6}>
                            <BTFormItem
                                label={
                                    <>
                                        Default Quickbooks Payroll Item{" "}
                                        <BTPopover
                                            content={
                                                "Select the Payroll Item you want used by default when creating QuickBooks time activities from this internal user's time clock shifts.  This selection can be overridden on individual shifts before sending to QuickBooks."
                                            }
                                            removeSpacing
                                        />
                                    </>
                                }
                            >
                                <BTSelect
                                    {...props}
                                    id="defaultPayrollItem"
                                    data-testid="defaultPayrollItem"
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                    treeData={entity.payrollItems.treeData}
                                    value={values.defaultPayrollItem}
                                />
                            </BTFormItem>
                        </BTCol>
                    </div>
                )}
            </PageSection>
        </>
    );
}
